// @ts-nocheck

import { observer, inject } from 'mobx-react';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { TLCode } from '../../common/enums';
import styled from '@emotion/styled';
import { Pharmacy } from '../../store/models/Pharmacy';
import { Municipality } from '../../store/models/Municipality';
import { Instance } from 'mobx-state-tree';
import { PharmacyDetails } from '../../store/models/PharmacyDetails';
import { SystemStatus } from '../../store/models/SystemStatus';
import LeaveRouteDirtyWrapper from '../common/LeaveRouteDirtyWrapper';
import TranslatedMessage from '../common/translation/TranslatedMessage';
import { Form } from 'react-final-form';
import { PharmacySelectionDetailsTransfer } from './PharmacySelectionDetailsTransfer';
import PharmacyMarker from '../common/PharmacyMarker';
import { Circle } from '@react-google-maps/api';
import { PharmacyDetailedStore, PharmacyStore, MunicipalityStore } from '../../store';
import proj4 from 'proj4';
import PharmagardeMap from '../common/map/PharmagardeMap';
import { TextInput } from '../common/input/basicInputs';
import { TransferCalculationResult } from '../../store/models/TransferCalculationResult';
import { PharmacyNeighbor } from '../../store/models';
import { Button, Classes, Tooltip } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { css } from 'glamor';

interface SimpleFusionTabProps extends WithTranslateProps {
  onSave: () => void;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  getPharmacyById: (id) => any;
  pharmacyUser: boolean;
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  MunicipalityStore?: Instance<typeof MunicipalityStore>;
}

interface SimpleFusionTabState {
  notification: string;
  pharmacyToCloseSelected?: Instance<typeof PharmacyDetails>;
  pharmacyToKeepSelected?: Instance<typeof PharmacyDetails>;
  userAddedCircles: any[] | undefined;
  openAddCircleDialog: boolean;
  userAddedCircleLat: number | undefined;
  userAddedCircleLng: number | undefined;
  sourceMunicipality?: Instance<typeof Municipality>;
  sourcePharmacyCount: number | undefined;
  sourcePharmacyMaxCount: number | undefined;
  closestNeighbor?: Instance<typeof PharmacyNeighbor>;
  closestNeighborText?: string;
  thresholdNbrInhabitantsInZone: number | undefined;
  targetMunicipality?: Instance<typeof Municipality>;
  targetPharmacyCount: number | undefined;
  defaultProtections: { pharmacyId: number; radius: number }[];
  showProtectionCircles: true;
  showDefaultProtectionCircles: true;
  showTemporaryProtectionCircles: true;
  zoom: number | undefined;
  mapCenterLat: number | undefined;
  mapCenterLong: number | undefined;
  map: google.maps.Map | undefined;
  openMunicipalityDialog: boolean;
  nbrOfNeighbors: number;
  initialZoomCalculated: boolean;
  newLat?: number;
  newLong?: number;
  transferCalculationResult?: Instance<typeof TransferCalculationResult>;
  isVerified: boolean;
  hasUserChangedZoomOrCenter: boolean;
}

@inject('MunicipalityStore', 'PharmacyDetailedStore', 'PharmacyStore')
@observer
class SimpleFusionTab extends React.Component<SimpleFusionTabProps, SimpleFusionTabState> {
  state = {
    notification: '',
    openMunicipalityDialog: false,
    mapCenterLat: 50.58603,
    mapCenterLong: 3.806026,
    isVerified: false,
    hasUserChangedZoomOrCenter: false,
  } as SimpleFusionTabState;

  _isMounted = false;

  wgs84 = 'EPSG:4326';
  lambert2008 = 'EPSG:3812';

  red = 'DC143C';
  green = '7CFC00';
  yellow = 'e9ff00';
  blue = '1528f2';
  protectionColor = '#fc0d05';
  defaultProtectionColor = '#ec9ff4';
  authorizedColor = '7CFC00';
  closeIcon = (showBullet: boolean) =>
    generateCustomIcon(20, 20, this.red, this.red, showBullet, '000000', false, '888888', '1', '1');
  moveIcon = (showBullet: boolean) =>
    generateCustomIcon(20, 20, this.green, this.green, showBullet, '000000', false, '888888', '1', '1');
  neighborIcon = (showBullet: boolean) =>
    generateCustomIcon(14, 14, this.yellow, this.yellow, showBullet, '000000', false, '888888', '1', '1');
  targetIcon = generateCustomIcon(20, 20, this.blue, this.blue, true, '000000', false, '888888', '1', '1');
  greyNeighborIcon = (showBullet: boolean) =>
    generateCustomIcon(14, 14, 'CACFD2', 'CACFD2', showBullet, '000000', false, '888888', '1', '1');

  yellowBackgroundColor = css({
    '.bp3-button': {
      backgroundColor: '#' + this.yellow,
    },
    '.bp3-button:hover': {
      backgroundColor: '#' + this.yellow,
    },
  });

  blueBackgroundColor = css({
    '.bp3-button': {
      backgroundColor: 'blue',
    },
    '.bp3-button:hover': {
      backgroundColor: 'blue',
    },
  });

  greenBackgroundColor = css({
    '.bp3-button': {
      backgroundColor: 'green',
    },
    '.bp3-button:hover': {
      backgroundColor: 'green',
    },
  });

  redBackgroundColor = css({
    '.bp3-button': {
      backgroundColor: '#' + this.red,
    },
    '.bp3-button:hover': {
      backgroundColor: '#' + this.red,
    },
  });

  async componentDidMount() {
    this._isMounted = true;
    this.setState({ nbrOfNeighbors: 20 });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  private openAddCircleToMarkerDialog = (lat: number, lng: number) => {
    this.setState({ openAddCircleDialog: true, userAddedCircleLat: lat, userAddedCircleLng: lng });
  };

  drawDefaultProtectionCircle = (neighbor, markers) => {
    let municipality = this.props.MunicipalityStore!.dataList.find((m) => m.municipalityId == neighbor.municipalityId);
    // default protection 100, 500, 1000 m for population >30K, [7.5K-30K], < 7.5K
    let defaultCircleRadius = 500;

    try {
      if (municipality && municipality.population > 30000) {
        defaultCircleRadius = 100;
      } else if (municipality && municipality.population < 7500) {
        defaultCircleRadius = 1000;
      }
    } catch (e) {
      console.log('error');
    }

    let defaultProtectionCircle = (
      <>
        {this.state.showDefaultProtectionCircles && (
          <Circle
            key={'min' + neighbor.pharmacyId}
            center={{ lat: neighbor.latitude, lng: neighbor.longitude }}
            radius={defaultCircleRadius}
            options={{
              fillColor: this.defaultProtectionColor,
              strokeWeight: 0.4,
              strokeOpacity: 0.5,
            }}
            onRightClick={(e) => {
              //@ts-ignore
              google.maps.event.trigger(this.state.map, 'rightclick', e);
            }}
          />
        )}
      </>
    );

    markers.push(defaultProtectionCircle);

    // protection after transfer or fusion
    if (this.hasProtection(neighbor)) {
      neighbor.protections
        .filter((p) => p.status === '+' && new Date(p.endDate) >= new Date())
        .forEach((element) => {
          let protectedCircle = (
            <>
              {this.state.showTemporaryProtectionCircles && (
                <Circle
                  key={'min' + neighbor.pharmacyId}
                  center={{ lat: neighbor.latitude, lng: neighbor.longitude }}
                  radius={element.range}
                  options={{
                    fillColor: this.protectionColor, //'red',

                    strokeWeight: 0.4,

                    strokeOpacity: 0.5,
                  }}
                  onRightClick={(e) => {
                    //@ts-ignore
                    google.maps.event.trigger(this.state.map, 'rightclick', e);
                  }}
                />
              )}
            </>
          );

          markers.push(protectedCircle);
        });
    }

    return { pharmacyId: neighbor.pharmacyId, radius: defaultCircleRadius };
  };

  hasProtection = (pharmacy) => {
    const res = pharmacy.protections.length > 0;
    return res;
  };

  SameOrNeighbor = async (municipality1, municipality2) => {
    let result = false;
    if (municipality1 == undefined || municipality2 == undefined) return false;
    if (municipality1.mainINSCode === municipality2.mainINSCode) return true;
    const neighbors: any = await this.props.MunicipalityStore!.getNeighbors(municipality1.municipalityId);
    if (neighbors === undefined) return false;
    const res = neighbors.filter((n) => n.neighborId === municipality2.municipalityId);
    result = res.length === 1;
    console.log('SameOrNeighbor res' + res);
    console.log('SameOrNeighbor result' + result);
    return result;
  };

  HasEnoughPharmacies = () => {
    let result = false;
    result = this.state.sourcePharmacyCount! > this.state.sourcePharmacyMaxCount!;
    return result;
  };

  ClosingPharmacyAtDistance = () => {
    let result = true;

    return result;
  };

  VerifyTransfer = async () => {
    let isVerified = false;

    if (this.state.pharmacyToCloseSelected !== undefined && this.state.pharmacyToKeepSelected !== undefined) {
      // ToKeep et ToClose dans la même commune ou dans communes voisines
      const compatibleMunicipalities = await this.SameOrNeighbor(
        this.state.sourceMunicipality,
        this.state.targetMunicipality,
      );
      // Commune ToClose: nbre de pharmacies après fermeture <= nbre max
      const enoughRemainingPharmacies = this.HasEnoughPharmacies();
      // ToClose à bonne distance
      const closingPharmacyAtDistance = this.ClosingPharmacyAtDistance();

      isVerified = compatibleMunicipalities && enoughRemainingPharmacies && closingPharmacyAtDistance;
      this._isMounted && this.setState({ isVerified: isVerified });
    }

    let result = {
      pharmacyToTransferId:
        this.state.pharmacyToCloseSelected === undefined ? undefined : this.state.pharmacyToCloseSelected!.pharmacyId,
      // newLatitude: newLat,
      // newLongitude: newLong,
      newMunicipality:
        this.state.pharmacyToCloseSelected === undefined ? undefined : this.state.pharmacyToCloseSelected.municipality,
      // distance: 50,
      // within100Meters: true,
      // insidePolygon: false,
      nbrPharmacies: 1,
      nbrMinPharmacies: 1,
      nbrMaxPharmacies: 7,
      closestPharmacyId: this.state.closestNeighbor ? this.state.closestNeighbor.neighborId : 0,
      closestPharmacyDistance: this.state.closestNeighbor ? this.state.closestNeighbor.distance : 0,
      thresholdNbrInhabitantsInZone: this.state.thresholdNbrInhabitantsInZone,
    } as Instance<typeof TransferCalculationResult>;
    result.nbrInhabitantsInZone =
      this.state.pharmacyToCloseSelected === undefined
        ? undefined
        : this.state.pharmacyToCloseSelected.municipality!.population;
    const municipalityPharmacyList = this.props.PharmacyStore!.dataList.filter(
      (ph) =>
        ph.municipalityId ==
        (this.state.pharmacyToCloseSelected === undefined
          ? undefined
          : this.state.pharmacyToCloseSelected.municipality!.municipalityId),
    );
    result.nbrPharmacies = municipalityPharmacyList.length; // nbr actuel dans l'entité cible

    this._isMounted && this.setState({ transferCalculationResult: result });
  };

  getIsDirty = () => {
    return false;
  };

  somethingToSave = () => {
    return true;
  };

  renderPharmacyMarkers = () => {
    const { pharmacyToKeepSelected, pharmacyToCloseSelected } = this.state;
    // const { pharmaciesList, groupList } = this.props;
    const pharmacyList: any = [];
    if (pharmacyToCloseSelected !== undefined) pharmacyList.push(pharmacyToCloseSelected);
    if (pharmacyToKeepSelected !== undefined) pharmacyList.push(pharmacyToKeepSelected);

    let markers: any = [];

    if (pharmacyToCloseSelected) {
      const pharmacyToCloseMarker = (
        <PharmacyMarker
          key={pharmacyToCloseSelected.pharmacyId}
          id={pharmacyToCloseSelected.pharmacyId}
          marker={pharmacyToCloseSelected}
          zIndex={100}
          icon={this.closeIcon(pharmacyToCloseSelected.level === 3)}
          groupEditMode={false}
          onAddCircle={this.openAddCircleToMarkerDialog}
          disableRightClick
          group={pharmacyToCloseSelected.group}
        />
      );
      markers.push(pharmacyToCloseMarker);

      if (this.state.closestNeighbor !== undefined) {
        let defaultProtectionCircle = (
          <Circle
            key={'influence_' + pharmacyToCloseSelected.pharmacyId}
            center={{ lat: pharmacyToCloseSelected.latitude!, lng: pharmacyToCloseSelected.longitude! }}
            radius={this.state.closestNeighbor!.distance! / 2}
            options={{
              fillColor: this.defaultProtectionColor,
              strokeWeight: 0.4,
              strokeOpacity: 0.5,
            }}
          />
        );

        markers.push(defaultProtectionCircle);
      }
    }

    if (pharmacyToKeepSelected) {
      const pharmacyToTransferMarker = (
        <PharmacyMarker
          key={pharmacyToKeepSelected.pharmacyId}
          id={pharmacyToKeepSelected.pharmacyId}
          marker={pharmacyToKeepSelected}
          zIndex={100}
          icon={this.moveIcon(pharmacyToKeepSelected.level === 3)}
          groupEditMode={false}
          onAddCircle={this.openAddCircleToMarkerDialog}
          disableRightClick
          group={pharmacyToKeepSelected.group}
        />
      );
      markers.push(pharmacyToTransferMarker);
    }

    if (pharmacyToCloseSelected && pharmacyToKeepSelected) {
      //@ts-ignore
      const neighbors1 = pharmacyToKeepSelected.pharmacyNeighbors
        .filter((n) => n.distance !== undefined && n.distance < 6000)
        .sort((a: any, b: any) => (a.distance > b.distance ? 1 : b.distance > a.distance ? -1 : 0))
        .slice(0, this.state.nbrOfNeighbors);

      //@ts-ignore
      const neighbors2 = pharmacyToCloseSelected.pharmacyNeighbors
        .filter((n) => n.distance !== undefined && n.distance < 6000)
        .sort((a: any, b: any) => (a.distance > b.distance ? 1 : b.distance > a.distance ? -1 : 0))
        .slice(0, this.state.nbrOfNeighbors);

      const neighbors = Array.from(new Set(neighbors1.concat(neighbors2)));

      neighbors.forEach(async (item) => {
        const isPharmacyLoaded = this.props.PharmacyDetailedStore!.getById(item.neighborId) !== undefined;
        if (!isPharmacyLoaded) await this.props.PharmacyDetailedStore!.loadElement(item.neighborId);

        const neighbor = this.props.PharmacyDetailedStore!.getByIdOrNew(item.neighborId);
        if (
          neighbor &&
          (pharmacyToCloseSelected === undefined || pharmacyToCloseSelected.pharmacyId != neighbor.pharmacyId) &&
          (pharmacyToKeepSelected === undefined || pharmacyToKeepSelected.pharmacyId != neighbor.pharmacyId)
        ) {
          const neighborMarker = (
            <PharmacyMarker
              key={neighbor.pharmacyId + '_NeighborId_' + neighbor.neighborId}
              id={neighbor.pharmacyId}
              marker={neighbor}
              zIndex={100}
              icon={
                neighbor.isActive
                  ? this.neighborIcon(neighbor.level === 3)
                  : this.greyNeighborIcon(neighbor.level === 3)
              }
              groupEditMode={false}
              protections={neighbor.protections.filter((p) => p.status === '+' && new Date(p.endDate) >= new Date())}
              onAddCircle={this.openAddCircleToMarkerDialog}
              disableRightClick
              group={neighbor.group}
            />
          );
          markers.push(neighborMarker);
          this.drawDefaultProtectionCircle(neighbor, markers);
        }
      });
    }

    return markers;
  };

  calculateZoom = (centerLat: number, centerLong: number, includeNeighbors: boolean = false) => {
    if (this.state.map !== undefined) {
      let currentMarkerPositions: google.maps.LatLng[] = [];
      var bounds = new google.maps.LatLngBounds();
      var hasBoundExtended = false;

      const pharmacyDetails = this.state.pharmacyToKeepSelected;

      if (pharmacyDetails !== undefined) {
        // Add selected pharmacy position
        currentMarkerPositions.push(
          new google.maps.LatLng(pharmacyDetails.latitude as number, pharmacyDetails.longitude as number),
        );

        if (includeNeighbors)
          //add neighbors position
          pharmacyDetails.pharmacyNeighbors
            .slice()
            .sort((a, b) =>
              (a.distance as number) > (b.distance as number)
                ? 1
                : (b.distance as number) > (a.distance as number)
                ? -1
                : 0,
            )
            .slice(0, this.state.nbrOfNeighbors)
            .forEach((item) => {
              var neighbor = this.props.PharmacyDetailedStore!.getByIdOrNew(item.neighborId);
              if (neighbor) {
                currentMarkerPositions.push(
                  new google.maps.LatLng(neighbor.latitude as number, neighbor.longitude as number),
                );
              }
            });
      }

      const pharmacyToCloseDetails = this.state.pharmacyToCloseSelected;

      if (pharmacyToCloseDetails !== undefined) {
        // Add selected pharmacy position
        currentMarkerPositions.push(
          new google.maps.LatLng(pharmacyToCloseDetails.latitude as number, pharmacyToCloseDetails.longitude as number),
        );

        if (includeNeighbors)
          //add neighbors position
          pharmacyToCloseDetails.pharmacyNeighbors
            .slice()
            .sort((a, b) =>
              (a.distance as number) > (b.distance as number)
                ? 1
                : (b.distance as number) > (a.distance as number)
                ? -1
                : 0,
            )
            .slice(0, this.state.nbrOfNeighbors)
            .forEach((item) => {
              var neighbor = this.props.PharmacyDetailedStore!.getByIdOrNew(item.neighborId);
              if (neighbor) {
                currentMarkerPositions.push(
                  new google.maps.LatLng(neighbor.latitude as number, neighbor.longitude as number),
                );
              }
            });
      }

      //Fit the bounds of the map to include the marker positions
      for (var i = 0; i < currentMarkerPositions.length; i++) {
        var marker = currentMarkerPositions[i];

        if (marker !== undefined) {
          bounds.extend(marker);
          hasBoundExtended = true;
        }
      }
      if (hasBoundExtended && this.state.map) {
        this.state.map.fitBounds(bounds);
        this.setState({ mapCenterLat: centerLat, mapCenterLong: centerLong });
        var newbounds = this.state.map.getBounds();
        if (newbounds === undefined) return;
        for (i = 0; i < currentMarkerPositions.length; i++) {
          if (!newbounds.contains(currentMarkerPositions[i])) {
            const mapZoom = this.state.map.getZoom();
            if (mapZoom === undefined) return;
            this.state.zoom = mapZoom - 1;
            break;
          }
        }
      }
    }
  };

  centerAndZoom = (centerLat: number, centerLong: number, includeNeighbors: boolean = false) => {
    this.setState({ hasUserChangedZoomOrCenter: false });
    this.calculateZoom(centerLat, centerLong, includeNeighbors);
  };

  public render() {
    const { translate, onSave, pharmacyList, getPharmacyById } = this.props;

    const { mapCenterLong, mapCenterLat, closestNeighborText, isVerified } = this.state;
    const initialZoomCalculated = this.state.initialZoomCalculated;

    const getPharmacyCountByMunicipality = (municipality: Instance<typeof Municipality>) => {
      return municipality.nbrPharmaciesMain;
    };

    const dirty = this.getIsDirty();

    const fromWGS84ToLambert2008 = (lat: number, long: number): [number, number] => {
      //lambert2008
      proj4.defs(
        'EPSG:3812',
        '+proj=lcc +lat_1=49.83333333333334 +lat_2=51.16666666666666 +lat_0=50.797815 +lon_0=4.359215833333333 +x_0=649328 +y_0=665262 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
      );
      const result = proj4(this.wgs84, this.lambert2008, [long, lat]);
      const x = result[0];
      const y = result[1];

      return [x, y];
    };

    const onPharmacyToCloseSelected = async (pharmacyToClose: Instance<typeof Pharmacy>) => {
      if (pharmacyToClose === undefined || pharmacyToClose.pharmacyId === undefined) return;
      const isPharmacyLoaded = this.props.PharmacyDetailedStore!.getById(pharmacyToClose.pharmacyId) !== undefined;
      if (!isPharmacyLoaded) await this.props.PharmacyDetailedStore!.loadElement(pharmacyToClose.pharmacyId)!;
      const pharmacyDetails = this.props.PharmacyDetailedStore!.getByIdOrNew(pharmacyToClose.pharmacyId);
      this._isMounted &&
        this.setState({
          pharmacyToCloseSelected: pharmacyDetails,
          sourcePharmacyCount: -1,
        });

      await this.props.PharmacyDetailedStore!.loadNeighboringPharmacies(pharmacyDetails.pharmacyId);
      const neighbors = pharmacyDetails.pharmacyNeighbors
        .filter((n) => n.distance < 5000 && n.Pharmacyid != pharmacyDetails.pharmacyId)
        .sort((a, b) => (a.distance > b.distance ? 1 : b.distance > a.distance ? -1 : 0));

      const closestNeighbor = neighbors[0];
      if (closestNeighbor != undefined) {
        const closestNeighborDistance = closestNeighbor.distance;
        if (closestNeighborDistance < 1000) {
          this._isMounted && this.setState({ closestNeighbor: closestNeighbor, thresholdNbrInhabitantsInZone: 2500 });
        } else if (closestNeighborDistance < 3000) {
          this._isMounted && this.setState({ closestNeighbor: closestNeighbor, thresholdNbrInhabitantsInZone: 2000 });
        } else if (closestNeighborDistance < 5000) {
          this._isMounted && this.setState({ closestNeighbor: closestNeighbor, thresholdNbrInhabitantsInZone: 1500 });
        } else {
          this._isMounted && this.setState({ closestNeighbor: closestNeighbor, thresholdNbrInhabitantsInZone: 0 });
        }
        const closestPharmacy = this.props.PharmacyStore!.getById(closestNeighbor.neighborId);
        if (closestPharmacy != undefined) {
          const city = closestPharmacy.city ? closestPharmacy.city : '';
          const closestPharmacyText =
            (closestPharmacy.apbNumber ? closestPharmacy.apbNumber + ' - ' : '') +
            closestPharmacy.name! +
            (' ' + closestPharmacy.address + ', ' + closestPharmacy.zipCode + ' ' + city);
          this._isMounted && this.setState({ closestNeighborText: closestPharmacyText });
        }
      }

      //Load neighbors to display
      await neighbors.slice(0, this.state.nbrOfNeighbors).forEach((item) => {
        if (item.neighborId !== undefined) {
          const isPharmacyLoaded = this.props.PharmacyDetailedStore!.getById(item.neighborId) !== undefined;
          if (!isPharmacyLoaded) this.props.PharmacyDetailedStore!.loadElement(item.neighborId);
        }
      });

      pharmacyDetails.pharmacyNeighbors.map((ph) => {
        var pharmacy = this.props.PharmacyStore!.getById(ph.pharmacyId);
        ph.municipality = this.props.MunicipalityStore!.getById(pharmacy!.municipalityId!);
      });

      //get source main municipality
      const closingPhMunicipality = this.props.MunicipalityStore!.getById(pharmacyDetails.municipalityId);
      this._isMounted && this.setState({ sourceMunicipality: closingPhMunicipality });
      if (closingPhMunicipality != undefined) {
        const closingPhMainMunicipality = this.props.MunicipalityStore!.dataList.find(
          (item) => item.isMain && item.mainINSCode == closingPhMunicipality.mainINSCode,
        );
        if (closingPhMainMunicipality != undefined) {
          var count = getPharmacyCountByMunicipality(closingPhMainMunicipality);
          var maxCount = Math.floor(closingPhMunicipality.population / 5000);
          this._isMounted &&
            this.setState({
              sourceMunicipality: closingPhMunicipality,
              sourcePharmacyCount: count,
              sourcePharmacyMaxCount: maxCount,
            });
        }
      }

      this.VerifyTransfer();
    };

    const onPharmacyToKeepSelected = async (pharmacyToTransfer: Instance<typeof Pharmacy>) => {
      if (pharmacyToTransfer === undefined || pharmacyToTransfer.pharmacyId === undefined) return;

      const isPharmacyLoaded = this.props.PharmacyDetailedStore!.getById(pharmacyToTransfer.pharmacyId) !== undefined;
      if (!isPharmacyLoaded) await this.props.PharmacyDetailedStore!.loadElement(pharmacyToTransfer.pharmacyId)!;
      const pharmacyDetails = this.props.PharmacyDetailedStore!.getByIdOrNew(pharmacyToTransfer.pharmacyId);
      this._isMounted &&
        this.setState({
          pharmacyToKeepSelected: pharmacyDetails,
          targetPharmacyCount: -1,
        });

      await this.props.PharmacyDetailedStore!.loadNeighboringPharmacies(pharmacyDetails.pharmacyId);
      const neighbors = pharmacyDetails.pharmacyNeighbors
        .filter((n) => n.distance < 5000 && n.Pharmacyid != pharmacyDetails.pharmacyId)
        .sort((a, b) => (a.distance > b.distance ? 1 : b.distance > a.distance ? -1 : 0));

      //Load neighbors to display
      await neighbors.slice(0, this.state.nbrOfNeighbors).forEach((item) => {
        if (item.neighborId !== undefined) {
          const isPharmacyLoaded = this.props.PharmacyDetailedStore!.getById(item.neighborId) !== undefined;
          if (!isPharmacyLoaded) this.props.PharmacyDetailedStore!.loadElement(item.neighborId);
        }
      });

      pharmacyDetails.pharmacyNeighbors.map((ph) => {
        var pharmacy = this.props.PharmacyStore!.getById(ph.pharmacyId);
        ph.municipality = this.props.MunicipalityStore!.getById(pharmacy!.municipalityId!);
      });

      //get source main municipality pharmacy count
      const targetMunicipality = this.props.MunicipalityStore!.getById(pharmacyDetails.municipalityId);

      if (targetMunicipality !== undefined) {
        var count = getPharmacyCountByMunicipality(targetMunicipality);
        this._isMounted && this.setState({ targetMunicipality: targetMunicipality, targetPharmacyCount: count });
      }

      this.VerifyTransfer();
    };

    return (
      <Container>
        <div>
          {
            //TODO : set inital values and TYPE of form
          }
          <Form
            initialValues={{}}
            onSubmit={onSave}
            render={() => (
              <FormContainer>
                <>
                  <DivLine>
                    <PharmacySelectionDetailsTransfer
                      pharmacyLabelCode={TLCode.TRANSFER_PharmacyToClose}
                      pharmacyList={pharmacyList.filter((item) => item.level >= 0) as Array<Instance<typeof Pharmacy>>}
                      onChange={onPharmacyToCloseSelected}
                      getPharmacyById={getPharmacyById}
                      pharmacySelected={this.state.pharmacyToCloseSelected}
                      pharmacyLabelColor={'red'}
                      withEntityAndNbrPharmacy={true}
                      nbrPharmacies={
                        this.state.sourcePharmacyCount != undefined && this.state.sourcePharmacyCount >= 0
                          ? this.state.sourcePharmacyCount
                          : undefined
                      }
                      translate={translate}
                      convertWGS84ToLambert2008={fromWGS84ToLambert2008}
                    />
                  </DivLine>
                  <DivLine style={{ marginTop: '20px', color: 'darkgrey', gap: '10px', textAlign: 'right' }}>
                    <span style={{ width: '100%' }}>
                      <TranslatedMessage id={TLCode.TRANSFER_ClosestPharmacy} />
                    </span>
                    <span
                      style={{
                        width: '350%',
                      }}
                    >
                      <TextInput value={closestNeighborText} disabled />
                    </span>
                    <span style={{ width: '100%' }}>
                      <TranslatedMessage id={TLCode.TRANSFER_Distance} />
                    </span>
                    <span
                      style={{
                        width: 'auto',
                      }}
                    >
                      <TextInput
                        value={
                          this.state.transferCalculationResult !== undefined &&
                          this.state.transferCalculationResult.closestPharmacyDistance !== undefined
                            ? this.state.transferCalculationResult.closestPharmacyDistance.toString()
                            : ''
                        }
                        style={{
                          width: '60px',
                        }}
                        disabled
                      />
                    </span>
                    <span style={{ width: '100%' }}>
                      <TranslatedMessage id={TLCode.TRANSFER_NbrInhabitantsInZone} />
                    </span>
                    <span
                      style={{
                        width: 'auto',
                      }}
                    >
                      <TextInput
                        value={
                          this.state.transferCalculationResult !== undefined &&
                          this.state.transferCalculationResult.thresholdNbrInhabitantsInZone !== undefined
                            ? this.state.transferCalculationResult.thresholdNbrInhabitantsInZone.toString()
                            : ''
                        }
                        style={{
                          width: '60px',
                        }}
                        disabled
                      />
                    </span>
                  </DivLine>
                  <DivLine>
                    {!isVerified && (
                      <p>
                        <TranslatedMessage id={TLCode.Rejected} />
                      </p>
                    )}
                    {isVerified && (
                      <p>
                        <TranslatedMessage id={TLCode.Authorized} />
                      </p>
                    )}
                  </DivLine>
                  <DivLine>
                    <PharmacySelectionDetailsTransfer
                      pharmacyLabelCode={TLCode.TRANSFER_PharmacyToKeep}
                      pharmacyList={pharmacyList.filter((item) => item.level >= 0) as Array<Instance<typeof Pharmacy>>}
                      onChange={onPharmacyToKeepSelected}
                      getPharmacyById={getPharmacyById}
                      pharmacySelected={this.state.pharmacyToKeepSelected}
                      pharmacyLabelColor={'green'}
                      pharmacyTextColor={'white'}
                      withEntityAndNbrPharmacy={true}
                      nbrPharmacies={
                        this.state.targetPharmacyCount != undefined && this.state.targetPharmacyCount >= 0
                          ? this.state.targetPharmacyCount
                          : undefined
                      }
                      translate={translate}
                      convertWGS84ToLambert2008={fromWGS84ToLambert2008}
                    />
                  </DivLine>
                </>
              </FormContainer>
            )}
          />
        </div>
        <LeaveRouteDirtyWrapper blockChange={dirty} />
        &nbsp;&nbsp;
        <DivLine style={{ height: '100%' }}>
          <DivColumn style={{ width: '100%', height: '100%', paddingRight: '5px' }}>
            <PharmagardeMap
              center={
                mapCenterLat === undefined || mapCenterLong === undefined
                  ? undefined
                  : { lat: mapCenterLat, lng: mapCenterLong }
              }
              onLoad={(map: google.maps.Map) => {
                this.setState({ map });
                this.setState({ initialZoomCalculated: true });
              }}
              gridSize={5}
              maximalZoom={18}
              minimalZoom={8}
              initialZoom={18}
            >
              {initialZoomCalculated
                ? this.state.userAddedCircles !== undefined
                  ? [...this.renderPharmacyMarkers(), ...this.state.userAddedCircles]
                  : [...this.renderPharmacyMarkers()]
                : undefined}
            </PharmagardeMap>
          </DivColumn>
          <DivColumn style={{ width: '150px' }}>
            <DivLine>
              <DivColumn style={{ width: '100%' }}>
                <DivLine style={{ width: '150px', marginTop: '10px' }}>
                  <Tooltip
                    className={Classes.TOOLTIP_INDICATOR}
                    content={translate(TLCode.TRANSFER_ZoomPharmacyKeep)}
                    hoverOpenDelay={1000}
                  >
                    <Button
                      icon={IconNames.LOCATE}
                      onClick={() => {
                        if (
                          this.state.pharmacyToKeepSelected !== undefined &&
                          this.state.pharmacyToKeepSelected.latitude !== undefined &&
                          this.state.pharmacyToKeepSelected.longitude !== undefined
                        ) {
                          this.centerAndZoom(
                            this.state.pharmacyToKeepSelected.latitude!,
                            this.state.pharmacyToKeepSelected.longitude!,
                            false,
                          );
                        }
                      }}
                      className={this.greenBackgroundColor.toString()}
                    />
                  </Tooltip>
                  <Tooltip
                    className={Classes.TOOLTIP_INDICATOR}
                    content={translate(TLCode.TRANSFER_ZoomHalfHalf)}
                    hoverOpenDelay={1000}
                  >
                    <Button
                      onClick={() => {
                        if (
                          this.state.pharmacyToKeepSelected !== undefined &&
                          this.state.pharmacyToKeepSelected.latitude !== undefined &&
                          this.state.pharmacyToKeepSelected.longitude !== undefined &&
                          this.state.pharmacyToCloseSelected !== undefined &&
                          this.state.pharmacyToCloseSelected.latitude !== undefined &&
                          this.state.pharmacyToCloseSelected.longitude !== undefined
                        ) {
                          this.centerAndZoom(
                            (this.state.pharmacyToKeepSelected.latitude! +
                              Number(this.state.pharmacyToCloseSelected.latitude!)) /
                              2,
                            (this.state.pharmacyToKeepSelected.longitude! +
                              Number(this.state.pharmacyToCloseSelected.longitude!)) /
                              2,
                            false,
                          );
                        }
                      }}
                      text="1/2"
                    />
                  </Tooltip>
                  <Tooltip
                    className={Classes.TOOLTIP_INDICATOR}
                    content={translate(TLCode.TRANSFER_ZoomClose)}
                    hoverOpenDelay={1000}
                  >
                    <Button
                      icon={IconNames.LOCATE}
                      onClick={() => {
                        if (
                          this.state.pharmacyToCloseSelected !== undefined &&
                          this.state.pharmacyToCloseSelected.latitude !== undefined &&
                          this.state.pharmacyToCloseSelected.longitude !== undefined
                        ) {
                          this.centerAndZoom(
                            Number(this.state.pharmacyToCloseSelected.latitude!),
                            Number(this.state.pharmacyToCloseSelected.longitude!),
                            false,
                          );
                        }
                      }}
                      className={this.redBackgroundColor.toString()}
                    />
                  </Tooltip>
                  <Tooltip
                    className={Classes.TOOLTIP_INDICATOR}
                    content={translate(TLCode.TRANSFER_ZoomNeighbors)}
                    hoverOpenDelay={1000}
                  >
                    <Button
                      icon={IconNames.LOCATE}
                      onClick={() => {
                        if (
                          this.state.pharmacyToKeepSelected !== undefined &&
                          this.state.pharmacyToKeepSelected.latitude !== undefined &&
                          this.state.pharmacyToKeepSelected.longitude !== undefined &&
                          this.state.pharmacyToCloseSelected !== undefined &&
                          this.state.pharmacyToCloseSelected.latitude !== undefined &&
                          this.state.pharmacyToCloseSelected.longitude !== undefined
                        ) {
                          this.centerAndZoom(
                            (this.state.pharmacyToKeepSelected.latitude! +
                              Number(this.state.pharmacyToCloseSelected.latitude!)) /
                              2,
                            (this.state.pharmacyToKeepSelected.longitude! +
                              Number(this.state.pharmacyToCloseSelected.longitude!)) /
                              2,
                            true,
                          );
                        }
                      }}
                      className={this.yellowBackgroundColor.toString()}
                    />
                  </Tooltip>
                </DivLine>
              </DivColumn>
            </DivLine>
          </DivColumn>
        </DivLine>
      </Container>
    );
  }
}

export default withTranslate(SimpleFusionTab);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 10px;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const SVGCODE_LOCATION: String =
  '<svg id="Layer_1" enable-background="new 0 0 32 32" width="SVG_WIDTH" height="SVG_HEIGHT" viewBox="0 0 510.993 510.993" xmlns="http://www.w3.org/2000/svg"> \
<g id="g869"> \
<path  \
    id="path19" \
    style="stroke-width:0;fill:%23DOWN_COLOR;fill-opacity:DOWN_OPACITY" \
    d="M 25.466797 5.7324219 L 8.2148438 19.822266 L 15.111328 31.537109 C 15.279597 31.822859 15.586442 31.998873 15.917969 32 L 15.921875 32 C 16.252212 32 16.556916 31.826589 16.726562 31.542969 L 25.271484 17.259766 C 26.402647 15.368546 27.000017 13.203834 27 11 C 26.999985 9.0545661 26.35663 7.3196657 25.466797 5.7324219 z " \
    transform="scale(15.968531)" /> \
<path \
    id="path30" \
    style="stroke-width:0;stroke-opacity:1;fill:%23UP_COLOR;fill-opacity:UP_OPACITY" \
    d="M 16 0 C 9.9347584 0 5 4.9347584 5 11 L 5 11.041016 C 5 13.215167 5.5843792 15.355026 6.6875 17.228516 L 8.2148438 19.822266 L 25.466797 5.7324219 C 23.582444 2.3711976 20.119745 -8.0438816e-16 16 0 z " \
    transform="scale(15.968531)" /> \
</g> \
\
<circle \
  style="fill:%23BULLET_COLOR;fill-opacity:BULLET_OPACITY;stroke-width:0" \
  id="path858" \
  cx="255.49649" \
  cy="172.12808" \
  r="71.486473" /> \
  <path \
 id="path860" \
 style="fill:%23CIRCLE_COLOR;fill-opacity:CIRCLE_OPACITY;stroke-width:0" \
 d="M 254.64312,61.732897 A 110.39448,110.39448 0 0 0 145.10091,172.11298 110.39448,110.39448 0 0 0 255.48098,282.52407 110.39448,110.39448 0 0 0 365.89209,172.11298 110.39448,110.39448 0 0 0 255.48098,61.732897 a 110.39448,110.39448 0 0 0 -0.83786,0 z m 0.83786,26.904563 a 83.489289,83.489289 0 0 1 83.50654,83.47552 83.489289,83.489289 0 0 1 -83.50654,83.50653 83.489289,83.489289 0 0 1 -83.4755,-83.50651 83.489289,83.489289 0 0 1 83.4755,-83.47554 z" />  \
 <g id="g869-5" \
 transform="matrix(0.97659549,0,0,0.97659549,421.08693,6.5502129)"> \
<path \
   id="path19-4" \
   style="fill:none;fill-opacity:1;stroke:%23BORDER_COLOR;stroke-width:13.0967;stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:BORDER_OPACITY" \
   d="m -169.35814,-0.01320192 c -96.853,0 -175.65384,78.80084392 -175.65384,175.65384192 v 0.65496 c 0,34.718 9.33167,68.8884 26.9469,98.80528 l 24.38944,41.41838 110.12671,187.06884 c 2.68701,4.56301 7.58688,7.37369 12.88088,7.39169 h 0.0624 c 5.275,0 10.14069,-2.76912 12.84968,-7.29811 L -21.306159,275.5999 C -3.2431479,245.3999 6.2959734,210.83263 6.295702,175.64064 6.2954625,144.57492 -3.9779718,116.87111 -18.187298,91.525155 -48.277647,37.85134 -103.57186,-0.01320192 -169.35814,-0.01320192 Z" /> \
</g> \
</svg>';

//colors are RVB six-digit hexadecimal
const generateIconURL = (
  width: number,
  height: number,
  upColor: string,
  downColor: string,
  showBullet: boolean,
  bulletColor: string,
  showCircle: boolean,
  circleColor: string,
  downOpacity: string,
  upOpacity: string,
) => {
  if (isNaN(width) || width <= 0 || width > 500) width = 32;
  if (isNaN(height) || height <= 0 || height > 500) width = 32;
  if (!upColor || upColor.length != 6) upColor = '000000';
  if (!upOpacity) upOpacity = '1';
  if (!downColor || downColor.length != 6) downColor = '000000';
  if (!downOpacity) downOpacity = '1';
  if (!bulletColor || bulletColor.length != 6) bulletColor = '000000';
  if (!circleColor || circleColor.length != 6) circleColor = '000000';

  var customizedSVG = SVGCODE_LOCATION;
  customizedSVG = customizedSVG.replace('BORDER_COLOR', '' + BORDER_COLOR);
  customizedSVG = customizedSVG.replace('BORDER_VISIBLE', '' + BORDER_VISIBLE ? '1' : '0');
  customizedSVG = customizedSVG.replace('SVG_WIDTH', '' + width);
  customizedSVG = customizedSVG.replace('SVG_HEIGHT', '' + height);
  customizedSVG = customizedSVG.replace('UP_COLOR', upColor);
  customizedSVG = customizedSVG.replace('UP_OPACITY', upOpacity);
  customizedSVG = customizedSVG.replace('DOWN_COLOR', downColor);
  customizedSVG = customizedSVG.replace('DOWN_OPACITY', downOpacity);
  customizedSVG = customizedSVG.replace('BULLET_COLOR', bulletColor);
  customizedSVG = customizedSVG.replace('CIRCLE_COLOR', circleColor);
  customizedSVG = customizedSVG.replace('BULLET_OPACITY', showBullet ? '1' : '0');
  customizedSVG = customizedSVG.replace('CIRCLE_OPACITY', showCircle ? '1' : '0');

  return 'data:image/svg+xml;utf-8, ' + customizedSVG;
};

export const generateCustomIcon = (
  width: number,
  height: number,
  upColor: string,
  downColor: string,
  showBullet: boolean,
  bulletColor: string,
  showCircle: boolean,
  circleColor: string,
  downOpacity: string,
  upOpacity: string,
) => {
  return {
    url: generateIconURL(
      width,
      height,
      upColor,
      downColor,
      showBullet,
      bulletColor,
      showCircle,
      circleColor,
      downOpacity,
      upOpacity,
    ),
  };
};

const BORDER_COLOR: string = '000000';
const BORDER_VISIBLE: boolean = true;
